import { isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../../hooks/react-redux';
import styles from '../../Analytics.module.scss';
import { DocumentAnalyticsChart, getAllDocumentsRiskTolerance, getEntityDocumentsRiskTolerance, getIncludePreExecutionAnalytics, getPreExecutionDocumentsRiskTolerance, getSelectedMyCompanyEntity, getShowOverrideRiskRating, toggleRecalculateConfirmationModal } from '../../store';
import { MinifiedRiskToleranceChart } from './MinifiedRiskToleranceChart';
import { OverflowTooltip } from '../../../../shared/tooltip';
import { IconButton } from '../../../../shared/button/IconButton';
import { Calculator } from '../../../../shared/icons';
import { getUserRole } from '../../../../auth/login/store';
import { adminRoles } from '../../../../constants/permittedRoles';

const { amber } = styles;

interface AllRiskToleranceChartsProps {
    tileInView: null | DocumentAnalyticsChart;
    setTileInView: (tile: DocumentAnalyticsChart | null) => void;
    dimensions: {
        height: number;
        width: number;
    };
}

export const AllRiskToleranceCharts: React.FC<AllRiskToleranceChartsProps> = ({
    setTileInView,
    tileInView,
    dimensions
}) => {
    const dispatch = useAppDispatch();
    const selectedCompanyEntityId = useAppSelector(getSelectedMyCompanyEntity);
    const entityId = useMemo(() => !isNull(selectedCompanyEntityId) ? selectedCompanyEntityId : undefined, [selectedCompanyEntityId]);
    const allDocumentsRiskTolerance = useAppSelector(getAllDocumentsRiskTolerance);
    const entityDocumentsRiskTolerance = useAppSelector(getEntityDocumentsRiskTolerance);
    const preExecutionDocumentsRiskTolerance = useAppSelector(getPreExecutionDocumentsRiskTolerance);
    const showOverrideRiskRating = useAppSelector(getShowOverrideRiskRating);
    const includePreExecutionAnalytics = useAppSelector(getIncludePreExecutionAnalytics);
    const userRole = useAppSelector(getUserRole);
    const isAdminUser = adminRoles.includes(userRole!);

    const recalculate = useCallback(() => dispatch(toggleRecalculateConfirmationModal(true)), [dispatch]);

    const divisibleBy = useMemo(() => {
        let divisibleBy = 1;
        if (!isUndefined(entityId)) {
            divisibleBy++;
        }
        if (includePreExecutionAnalytics) {
            divisibleBy++;
        }
        return divisibleBy;
    }, [entityId, includePreExecutionAnalytics]);
    const chartDimensions = useMemo(() => ({ height: (dimensions.height - 30) / divisibleBy, width: dimensions.width }), [dimensions, divisibleBy]);

    const showCalculateButton = useMemo(() => isAdminUser && [allDocumentsRiskTolerance, entityDocumentsRiskTolerance, preExecutionDocumentsRiskTolerance].some(({ riskHasUpdated, isRecalculating }) => riskHasUpdated && !isRecalculating), [allDocumentsRiskTolerance, entityDocumentsRiskTolerance, preExecutionDocumentsRiskTolerance, isAdminUser]);
    return (
        <div className={styles.allRiskToleranceChartsWrapper} style={dimensions}>
            <div className={styles.allRiskToleranceChartsHeader}><OverflowTooltip className={styles.documentRiskToleranceChartTitle} overlayText='Risk Tolerance' /></div>
            <div className={styles.allRiskToleranceCharts}>
                <MinifiedRiskToleranceChart tile={DocumentAnalyticsChart.DOCUMENT_RISK_TOLERANCE} tileInView={tileInView} setTileInView={setTileInView} allDocumentsRiskTolerance={allDocumentsRiskTolerance} dimensions={chartDimensions} marginBottom={divisibleBy > 1 ? '20px' : undefined} showOverrideRiskRating={showOverrideRiskRating} />
                {!isUndefined(entityId) && <MinifiedRiskToleranceChart tile={DocumentAnalyticsChart.ENTITY_DOCUMENT_RISK_TOLERANCE} tileInView={tileInView} setTileInView={setTileInView} allDocumentsRiskTolerance={entityDocumentsRiskTolerance} dimensions={chartDimensions} entityId={entityId} marginBottom={includePreExecutionAnalytics ? '20px' : undefined} showOverrideRiskRating={showOverrideRiskRating} />}
                {includePreExecutionAnalytics && <MinifiedRiskToleranceChart tile={DocumentAnalyticsChart.PRE_EXECUTION_DOCUMENT_RISK_TOLERANCE} tileInView={tileInView} setTileInView={setTileInView} allDocumentsRiskTolerance={preExecutionDocumentsRiskTolerance} dimensions={chartDimensions} isPreExecution showOverrideRiskRating={showOverrideRiskRating} />}
            </div>
            {showCalculateButton &&
                <div className={styles.recalculateWrapper}>
                    <IconButton icon={Calculator} onClick={recalculate} color={amber} overlayText='Recalculate Risk Tolerance' />
                </div>
            }
        </div>
    );
};
